import { HttpHeaders } from '@angular/common/http';
import Graphic from '@arcgis/core/Graphic';
import { JepGeoJSONLayerProperties } from '@jeppesen-foreflight/jmkw-types';
import { PluginIDs } from '../plugin-ids.type';
import { Feature } from 'maplibre-gl';
import { VectorStyle } from './layer-type-services/vector-tile/twc-vector-tile.layer';

export type TwcProtocol = 'VectorTile' | 'WebTile';

export interface TwcProduct {
  productName: string;
  label: string;
  weatherType: string;
  description: string;
  series: 'Forecast' | 'Observation';
  timeSlicePeriodInMinutes: number;
  lodMax: number;
  initialOpacity?: number;
  protocol: TwcProtocol;
  inventoryURL?: string;
  id: PluginIDs;
  defaultProductId: string; // same as noFlightLevel (default flight level)
  forecastTimeSlicePeriod: number;
  flightLevelProductIds: FlightLevelProductId;
  zOrder: number;
  layerSpecs?: {
    vectorTile?: { vectorStyle?: VectorStyle };
    graphics?: { convert2Graphics?: (features?: Feature[]) => Graphic[] };
    // TWC returns a mixed collection of features. GeoJSON can only handle one geometryType and it MUST be specified.
    geoJSON?: JepGeoJSONLayerProperties & { declutter?: boolean };
  };
}

export type FlightLevelProductId = Record<string, string>;

export const COMPONENT_NAME = 'MapKit';
export const DEFAULT_HTTP_HEADERS = new HttpHeaders({
  request_origin: COMPONENT_NAME
});

const WX_PRODUCTS: TwcProduct[] = [
  {
    id: 'lightning',
    defaultProductId: '403',
    productName: 'Weather-Lightning-TWC',
    label: 'Lightning',
    weatherType: 'LIGHTNING',
    description: 'TWC product (vector) - Global Lightning',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 40
  },
  {
    id: 'government-sigmets',
    defaultProductId: '6003-boeing',
    productName: 'Weather-GOV-SIGMETS-TWC',
    label: 'Government Sigmets',
    weatherType: 'SIGMET',
    description: 'TWC product (vector) - Government SIGMETs',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 0,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 70
  },
  {
    id: 'sea-surface-temperature',
    defaultProductId: 'seaSurfaceTemperature-boeing',
    productName: 'Weather-Sea-Surface-Temp-Forecast-TWC',
    label: 'Sea Surface Temperature',
    weatherType: 'SEA_SURFACE_TEMPS',
    description:
      'TWC product (raster, forecast) - Global Sea Surface Temperature Analysis',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 60,
    lodMax: 4,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 180,
    flightLevelProductIds: {},
    zOrder: 10
  },
  {
    id: 'tropical-cyclone-tracks',
    defaultProductId: '9066',
    productName: 'Weather-Tropical-Storm-Tracks-TWC',
    label: 'Tropical Cyclone Tracks',
    weatherType: 'TROPICAL_STORM_TRACKS',
    description: 'TWC product (vector) - Global Tropical Storm Tracks',
    series: 'Observation',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 90
  },
  {
    id: 'flight-conditions',
    defaultProductId: 'flightrules-ifr-boeing',
    productName: 'Weather-Flight-Rules-IFR-TWC',
    label: 'Flight Conditions',
    weatherType: 'FLT_RULES_IFR',
    description:
      'TWC product (raster, forecast) - Global IFR Flight Conditions',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 60,
    flightLevelProductIds: {},
    zOrder: 55
  },
  {
    id: 'global-water-vapor-satellite',
    defaultProductId: 'sat-wv-boeing',
    productName: 'Weather-Water-Vapor-Sat-TWC',
    label: 'Global Water Vapor Satellite',
    weatherType: 'SAT_VAPOR',
    description: 'TWC product (raster) - Global Water Vapor Satellite Image',
    series: 'Observation',
    timeSlicePeriodInMinutes: 15,
    lodMax: 7,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 35
  },
  {
    id: 'pireps',
    defaultProductId: '6009',
    productName: 'Weather-PIREPS-TWC',
    label: 'PIREPs',
    weatherType: 'PIREP',
    description: 'TWC product (vector) - PIREP',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 7,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 70
  },
  {
    id: 'temps-aloft',
    defaultProductId: '1214',
    productName: 'Weather-TempsAloft-TWC',
    label: 'Temps Aloft',
    weatherType: 'TEMPS_ALOFT',
    description: 'TWC product (vector, forecast) - Temps Aloft Forecast',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 3,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 360,
    flightLevelProductIds: {
      FL050: '1210',
      FL100: '1211',
      FL140: '1212',
      FL180: '1213',
      FL240: '1214',
      FL300: '1215',
      FL340: '1216',
      FL390: '1217',
      FL450: '1218',
      FL530: '1219'
    },
    zOrder: 95
  },
  {
    id: 'volcanic-ash-concentration',
    defaultProductId: '721',
    productName: 'Weather-Volcanic-Ash-Concentration-Charts',
    label: 'Volcanic Ash Concentration',
    weatherType: 'Volcanic_Ash_Concentration_Charts',
    description: 'TWC product (vector) - Volcanic Ash Concentration Charts',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 0,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 9
  },
  {
    id: 'volcanic-ash-clouds',
    defaultProductId: '6016',
    productName: 'Volcanic Ash Clouds',
    label: 'Volcanic Ash Clouds',
    weatherType: 'VAAC',
    description: 'TWC product (vector) - Volcanic Ash Clouds',
    series: 'Observation',
    timeSlicePeriodInMinutes: 180,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 75
  },
  {
    id: 'volcanic-points',
    defaultProductId: '6015',
    productName: 'Volcanic Points',
    label: 'Volcanic Points',
    weatherType: 'VAAC',
    description: 'TWC product (vector) - Volcanic Points',
    series: 'Observation',
    timeSlicePeriodInMinutes: 180,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 75
  },
  {
    id: 'global-ir-satellite-composite',
    defaultProductId: 'sat-ir-boeing',
    productName: 'Weather-Sat-IR-TWC',
    label: 'Global IR Satellite Composite',
    weatherType: 'SAT_IR',
    description: 'TWC product (raster) - Global IR Satellite Composite',
    series: 'Observation',
    timeSlicePeriodInMinutes: 30,
    lodMax: 7,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 35
  },
  {
    id: 'lifr',
    defaultProductId: 'flightrules-lifr-boeing',
    productName: 'Weather-Flight-Rules-LIFR-TWC',
    label: 'LIFR',
    weatherType: 'FLT_RULES_LIFR',
    description:
      'TWC product (raster, forecast) - Global LIFR Flight Conditions',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 60,
    flightLevelProductIds: {},
    zOrder: 56
  },
  {
    id: 'radar-reflectivity',
    defaultProductId: 'radarFcst-boeing',
    productName: 'Weather-Radar-Forecast-TWC',
    label: 'Radar Reflectivity',
    weatherType: 'RADAR_FCST',
    description:
      'TWC product (raster, forecast) - Radar Reflectivity Forecast w/ Winter Overlay',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 15,
    lodMax: 7,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 15,
    flightLevelProductIds: {},
    zOrder: 35
  },
  {
    id: 'wave-height',
    defaultProductId: 'significantHeightofCombinedWindWavesAndSwell',
    productName: 'Weather-Wave-Height-ECMWF-TWC',
    label: 'Wave Height',
    weatherType: 'WAVE_HEIGHT_FCST',
    description:
      'TWC product (raster, forecast) - ECMWF Global Wave Height Analysis',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 720,
    lodMax: 9,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 360,
    flightLevelProductIds: {},
    zOrder: 10
  },
  {
    id: 'metars',
    defaultProductId: '6000',
    productName: 'Weather-SurfaceConditions-TWC',
    label: 'METARs',
    weatherType: 'METAR',
    description: 'TWC product (vector) - METAR',
    series: 'Observation',
    timeSlicePeriodInMinutes: 60,
    lodMax: 7,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 70
  },
  {
    id: 'icing',
    defaultProductId: 'fip-FL240-boeing', //'fip-FLxxx-boeing',
    productName: 'Weather-Icing-TWC',
    label: 'Icing',
    weatherType: 'ICING',
    description: 'TWC product (raster, forecast) - Icing Forecast',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 180,
    flightLevelProductIds: {
      FL010: 'fip-FL010-boeing',
      FL020: 'fip-FL020-boeing',
      FL030: 'fip-FL030-boeing',
      FL050: 'fip-FL050-boeing',
      FL060: 'fip-FL060-boeing',
      FL080: 'fip-FL080-boeing',
      FL100: 'fip-FL100-boeing',
      FL120: 'fip-FL120-boeing',
      FL140: 'fip-FL140-boeing',
      FL160: 'fip-FL160-boeing',
      FL180: 'fip-FL180-boeing',
      FL210: 'fip-FL210-boeing',
      FL240: 'fip-FL240-boeing'
    },
    zOrder: 85
  },
  {
    id: 'radar-mosaic-with-winter-overlay',
    defaultProductId: 'radarMosaic-boeing',
    productName: 'Weather-RadarMosaic-TWC',
    label: 'Radar Mosaic with Winter Overlay',
    weatherType: 'RADAR',
    description:
      'TWC product (raster) - Global Radar Mosaic with Winter Overlay',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 9,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 35
  },
  {
    id: 'high-ice-water-content',
    defaultProductId: 'highIceWaterContent-boeing',
    productName: 'Weather-High-Ice-Water-TWC',
    label: 'High Ice Water Content',
    weatherType: 'HIGH_ICE_WATER',
    description:
      'TWC product (raster, forecast) - Global High Ice Water Content (HIWC) Forecast',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 60,
    flightLevelProductIds: {},
    zOrder: 10
  },
  {
    id: 'radar-echo-tops',
    defaultProductId: 'echotopsNowradCONUS-boeing',
    productName: 'Weather-EchoTops-TWC',
    label: 'Radar Echo Tops',
    weatherType: 'ECHO_TOPS',
    description: 'TWC product (raster) - Radar Echo Tops',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 9,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 40
  },
  {
    id: 'volcano-point-advisorie',
    defaultProductId: '6015',
    productName: 'Volcano Point Advisorie',
    label: 'Volcano Point Advisorie',
    weatherType: 'VAAC',
    description: 'TWC product (vector) - Volcano Point Advisories',
    series: 'Observation',
    timeSlicePeriodInMinutes: 180,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 75
  },
  {
    id: 'surface-analysis',
    defaultProductId: '1264',
    productName: 'Weather-Surface-Analysis-TWC',
    label: 'Surface Analysis',
    weatherType: 'SURFACE_ANALYSIS',
    description: 'TWC product (vector) - Global Surface Analysis',
    series: 'Observation',
    timeSlicePeriodInMinutes: 180,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 25
  },
  {
    id: 'winds-aloft',
    defaultProductId: '1204', // 'winds-aloft-boeing',
    productName: 'Weather-WindsAloft-TWC',
    label: 'Winds Aloft',
    weatherType: 'WINDS_ALOFT',
    description: 'TWC product (vector, forecast) - Winds Aloft Forecast',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 360,
    flightLevelProductIds: {
      FL050: '1200',
      FL100: '1201',
      FL140: '1202',
      FL180: '1203',
      FL240: '1204',
      FL300: '1205',
      FL340: '1206',
      FL390: '1207',
      FL450: '1208',
      FL530: '1209'
    },
    zOrder: 90
  },
  {
    id: 'thermal-satellite',
    defaultProductId: 'thermalSat',
    productName: 'Weather-Thermal-Sat-TWC',
    label: 'Thermal Satellite',
    weatherType: 'SAT_THERMAL',
    description: 'TWC product (raster) - Thermal Satellite Image',
    series: 'Observation',
    timeSlicePeriodInMinutes: 15,
    lodMax: 7,
    protocol: 'WebTile',
    inventoryURL:
      'https://api.weather.com/v3/TileServer/series/productSet/aviation', // only different one
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 35
  },
  {
    id: 'precipitation',
    defaultProductId: 'precip24hrAccum-boeing',
    productName: 'Weather-Precipitation-TWC',
    label: 'Precipitation',
    weatherType: 'PRECIP',
    description: 'TWC product (raster) - Accumulated Precipitation',
    series: 'Observation',
    timeSlicePeriodInMinutes: 15,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 20
  },
  {
    id: 'turbulence',
    defaultProductId: 'globalGTG-FL240-boeing', // 'globalGTG-FLxxx-boeing',
    productName: 'Weather-Turbulence-TWC',
    label: 'Turbulence',
    weatherType: 'TURBULENCE',
    description: 'TWC product (raster, forecast) - Turbulence Forecast',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 180,
    flightLevelProductIds: {
      FL110: 'globalGTG-FL110-boeing',
      FL130: 'globalGTG-FL130-boeing',
      FL150: 'globalGTG-FL150-boeing',
      FL180: 'globalGTG-FL180-boeing',
      FL210: 'globalGTG-FL210-boeing',
      FL240: 'globalGTG-FL240-boeing',
      FL270: 'globalGTG-FL270-boeing',
      FL300: 'globalGTG-FL300-boeing',
      FL340: 'globalGTG-FL340-boeing',
      FL390: 'globalGTG-FL390-boeing',
      FL430: 'globalGTG-FL430-boeing'
    },
    zOrder: 85
  },
  {
    id: 'surface-weather',
    defaultProductId: '1266',
    productName: 'Weather-Surface-Forecast-TWC',
    label: 'Surface Weather',
    weatherType: 'SURFACE_FORECAST',
    description: 'TWC product (vector) - Global Surface Forecasts',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 360,
    flightLevelProductIds: {},
    zOrder: 25
  },
  {
    id: 'high-level-significant',
    defaultProductId: '1262',
    productName: 'Weather-High-Level-Significant-Forecast-TWC',
    label: 'High Level Significant',
    weatherType: 'HI_LVL_SIG_WX_FCST',
    description:
      'TWC product (vector) - High Level Significant Weather Forecast',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 75
  },
  {
    id: 'global-ir-satellite-radar',
    defaultProductId: 'sat-vis-boeing',
    productName: 'Weather-Sat-Visible-TWC',
    label: 'Global IR Satellite + Radar',
    weatherType: 'SAT_VIS',
    description: 'TWC product (raster) - Global Visible Satellite Imagery',
    series: 'Observation',
    timeSlicePeriodInMinutes: 60,
    lodMax: 7,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 35
  },
  {
    id: 'mvfr',
    defaultProductId: 'flightrules-mvfr-boeing',
    productName: 'Weather-Flight-Rules-MVFR-TWC',
    label: 'MVFR',
    weatherType: 'FLT_RULES_MVFR',
    description:
      'TWC product (raster, forecast) - Global MVFR Flight Conditions',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 5,
    protocol: 'WebTile',
    forecastTimeSlicePeriod: 60,
    flightLevelProductIds: {},
    zOrder: 57
  },
  {
    id: 'tafs',
    defaultProductId: '6018',
    productName: 'Weather-TAFS-TWC',
    label: 'TAFs',
    weatherType: 'TAF',
    description: 'TWC product (vector) - Terminal Aerodrome Forecast (TAF)',
    series: 'Forecast',
    timeSlicePeriodInMinutes: 360,
    lodMax: 7,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 360,
    flightLevelProductIds: {},
    zOrder: 95
  },
  {
    id: 'airmets',
    defaultProductId: '6002-boeing',
    productName: 'Weather-AIRMETS-TWC',
    label: 'AIRMETs',
    weatherType: 'AIRMET',
    description: 'TWC product (vector) - Government AIRMETs',
    series: 'Observation',
    timeSlicePeriodInMinutes: 5,
    lodMax: 0,
    protocol: 'VectorTile',
    forecastTimeSlicePeriod: 0,
    flightLevelProductIds: {},
    zOrder: 70
  }
];
